import { useGridApiRef } from "@mui/x-data-grid-premium";
import {
  usePersistMuiTableState,
  MaterialUiColumnsProps,
  LaunchPage,
  MaterialUiTable,
  sortByDateTime,
} from "@qivia/ui";
import { useState, useCallback, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { documentDownloadXlsxAsync } from "../../../libSlice/downloadFileXslx";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import {
  VehicleElectrification,
  VehicleElectrificationDisplayed,
} from "./electrificationApi";
import { formatDate, getDate } from "../../../libSlice/materialUiFormats";
import { selectListAllVehiclesInDb } from "../vehicles/vehiclesSlice";
import "../../../i18n/i18n";

export const VehiclesTab = () => {
  const params = useParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const vehiclesListInit = useAppSelector(selectListAllVehiclesInDb);

  const [listDisplayed, setListDisplayed] = useState<
    VehicleElectrificationDisplayed[] | null
  >(null);

  const pageName = "elecVehicles";
  const apiRef = useGridApiRef();
  const key = "customers-grid";

  const aggregation = {
    model: {
      reference: "size",
      registrationNumber: "size",
      model: "size",
      startDate: "size",
      endDate: "size",
    },
  };
  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );
  const vehiclesList: VehicleElectrification[] = useMemo(() => {
    const rowsWithId = vehiclesListInit.map((av) => {
      const newDict: VehicleElectrification & { id: string } = {
        ...av,
        distanceMax: 0,
        usualDistance: 0,
        consumptionMax: 0,
        usualConsumption: 0,
        optimalAutonomy: 0,
        possibleChargingPointsNumber: 0,
        startDate: new Date().toISOString(),
        endDate: new Date().toISOString(),
        id: av.uuid,
      };
      return newDict;
    });
    return sortByDateTime(rowsWithId, "desc", "startDate");
  }, [vehiclesListInit]);

  //TO REMOVE LATER
  useEffect(() => {
    if (vehiclesList) {
      const vehiclesSorted = sortByDateTime(vehiclesList, "desc", "startDate");
      setListDisplayed(vehiclesSorted);
    }
  }, [vehiclesList]);

  const searchBarProps = {
    values: vehiclesList,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["uuid" as const],
  };

  const downloadViewAction = useCallback(
    (file: Buffer | undefined) => {
      if (!file) return;
      void dispatch(
        documentDownloadXlsxAsync({
          buffer: file,
          fileName: "QIVIA_" + t("vehicles.title") + ".xlsx",
        }),
      );
    },
    [t, dispatch],
  );

  const columnsDisplayed: {
    [index in keyof VehicleElectrificationDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      reference: {
        type: "string",
      },
      registrationNumber: {
        type: "string",
      },
      model: {
        type: "string",
      },
      startDate: {
        type: "date",
        valueGetter: getDate,
        valueFormatter: formatDate,
      },
      endDate: {
        type: "date",
        valueGetter: getDate,
        valueFormatter: formatDate,
      },
      // prioritizationScore: {
      //   type: "number",
      // },
      // optimalBatteryCapacity: {
      //   type: "number",
      //   width: 220,
      // },
      // tripsScore: {
      //   type: "number",
      // },
      // distanceScore: {
      //   type: "number",
      // },
      // driverElectroCompatibilityScore: {
      //   type: "number",
      //   width: 270,
      // },
      distanceMax: {
        type: "number",
      },
      usualDistance: {
        type: "number",
      },
      consumptionMax: {
        type: "number",
      },
      usualConsumption: {
        type: "number",
      },
      optimalAutonomy: {
        type: "number",
      },
      possibleChargingPointsNumber: {
        type: "number",
      },
      analyticCode: {
        type: "string",
        width: 200,
      },
    };
  }, []);

  if (!listDisplayed) return <LaunchPage hasBorderRadius={true} />;

  if (params.tab !== "vehicles") {
    return;
  }

  return (
    <MaterialUiTable<
      keyof VehicleElectrificationDisplayed,
      VehicleElectrificationDisplayed
    >
      rows={listDisplayed}
      columns={columnsDisplayed}
      pageName={pageName}
      dataGridState={dataGridState}
      searchBar={searchBarProps}
      apiRef={apiRef}
      downloadViewAction={downloadViewAction}
    />
  );
};
